//Angular Default imports
import { AppInitService } from './digigov/services/app-init.service';
import { BrowserModule } from '@angular/platform-browser';
import {
  NgModule,
  CUSTOM_ELEMENTS_SCHEMA,
  APP_INITIALIZER,
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
} from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule, TitleStrategy } from '@angular/router';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  MatMomentDateModule,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
  MomentDateModule,
} from '@angular/material-moment-adapter';
import { AppComponent } from './app.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { OverlayModule } from '@angular/cdk/overlay';
import {NgIdleKeepaliveModule} from '@ng-idle/keepalive'
// External Angular Libraries
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ToastrModule, ToastContainerModule } from 'ngx-toastr';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { NgSelectModule } from '@ng-select/ng-select';
import { TreeModule } from '@circlon/angular-tree-component';
import { NgxNavbarModule } from 'ngx-bootstrap-navbar';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { CKEditorModule } from 'ckeditor4-angular';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { NgChartsModule } from 'ng2-charts';
import { NgxMaskModule } from 'ngx-mask';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { AngularSplitModule } from 'angular-split';

// Angular Material
import { MaterialModule } from './material.module';

// Digigov Guards
import { RoleGuardService } from './digigov/guards/role-guard.service';
import { AuthGuardService } from './digigov/guards/auth-guard.service';

// Digigov Interceptor
import { HttpInterceptorProviders } from './digigov/interceptors/index';
import { httpInterceptorDialogComponent } from './digigov/services/message-display/message-display.service';

// Digigov Services
import { CustomTranslateService } from './digigov/services/custom-translate.service';
import { AuthenticationService } from './digigov/services/authentication.service';

// Digigov Config
import { ComponentConfig } from './config/component.config';

//Digigov Core Component
import { DgFileUploadLibModule } from 'dg-file-upload-lib';
import { DgMenuLibModule } from 'dg-menu-lib';
import { DgEmpSearchLibModule } from 'dg-emp-search-lib';
import { DgTreeLibModule } from 'dg-tree-lib';
import { DgOrgSearchLibModule } from 'dg-org-search-lib';
import { DgListboxLibModule } from 'dg-listbox-lib';
import { DgGridLibModule } from 'dg-grid-lib';
import { DgCardGridLibModule } from 'dg-card-grid-lib';
import { DgInputLibModule } from 'dg-input-lib';
import { DgDynamicGridLibModule } from 'dg-dynamic-grid-lib';
import { DgAddressLibModule } from 'dg-address-lib';
import { Bs4TooltipsModule } from './digigov/directives/bs4-tooltips/bs4-tooltips.module';
import { DgPostSearchLibModule } from 'dg-post-search-lib';
import { DgFabSpeedDialLibModule } from 'dg-fab-speed-dial-lib';
import { DgNavBarLibModule } from 'dg-nav-bar-lib';
// Digigov Application Service
import { ComponentLoaderService } from './component-loader.service';

//Application Component
import { IntermediateSaveService } from './digigov/services/intermediate-save.service';
import { ConfirmationDialogComponent } from './digigov/dg_core/confirmation-dialog/confirmation-dialog.component';
import { AppRoutingModule } from './app-routing.module';
import { StyleManagerService } from '../app/digigov/services/theme-manager.service';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { InjectableRxStompConfig, RxStompService } from '@stomp/ng2-stompjs';
import { StompWebsocketService } from './digigov/services/stomp-websocket.service';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';
import { DgNgCalendarLibModule } from 'dg-ng-calendar-lib';
import { MainService } from './digigov/dg_core/main/main.service';
import { CommonPipeModule } from './digigov/pipes/common-pipe.module';
import { OAuthModule, OAuthStorage } from 'angular-oauth2-oidc';
import { HeaderComponent } from './digigov/dg_core/header/header.component';
import { MainComponent } from './digigov/dg_core/main/main.component';
import { footerModule } from './digigov/dg_core/footer/footer.module';
import { DgNotifypopComponent } from './digigov/dg_core/dg-notifypop/dg-notifypop.component';
import { DgSharedModule } from './digigov/dg_core/dg-shared.module';
import { DgTitleStrategyService } from './digigov/services/dg-title-strategy.service';
import { UnauthorizedPageComponent } from './digigov/dg_core/unauthorized-page/unauthorized-page.component';
import { EncryptionService } from './digigov/services/encryption.service';
import { customOauth2Config } from 'src/assets/config/oauth2-config';
import { AuthType, CaptchaType } from './digigov/constants/digigov.constants';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { DgFontresizerComponent } from './digigov/dg_core/dg-fontresizer/dg-fontresizer.component';
import { DG_ORG_INPUTS } from 'dg-org-search-lib';
import { DSignStompWebsocketService } from './digigov/services/d-sign-stomp-websocket.service';
import { CfmsSharedModule } from './cfms-shared/cfms-shared.module';
// import { G2SanitizedInputDirective } from './XSSdirective';

export function oauth2StorageFactory(): OAuthStorage {
  return customOauth2Config.storageType;
}

export function initApp(appInitService: AppInitService) {
  return () => {
    return appInitService.initAppConfig('./assets/config/env.json').pipe(
      switchMap(() =>
        appInitService.registerCulture(appInitService.locale).pipe(
          switchMap(() => {
            return appInitService
              .fetchThemes('./assets/my-theme/dg-theme-list.json')
              .pipe(
                switchMap(() => {
                  if (appInitService?.appConfig?.authType === AuthType.OATUH2) {
                    return appInitService.runInitialLoginSequence();
                  } else {
                    return of(true);
                  }
                })
              );
          })
        )
      ),
      catchError((e) => throwError(e))
    );
  };
}

@NgModule({
  declarations: [
    AppComponent,
    httpInterceptorDialogComponent,
    ConfirmationDialogComponent,
    DgNotifypopComponent,
    HeaderComponent,
    MainComponent,
    UnauthorizedPageComponent,
    // G2SanitizedInputDirective
    // Digigov Application Component
  ],
  imports: [
    NgIdleKeepaliveModule.forRoot(),
    DgFontresizerComponent,
    CfmsSharedModule,
    CommonModule,
    BrowserModule,
    MaterialModule.forRoot(),
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        //useFactory: HttpLoaderFactory,
        useClass: CustomTranslateService,
        deps: [HttpClient, AuthenticationService],
      },
    }),
    RouterModule.forRoot([]),
    BsDropdownModule.forRoot(),
    ReactiveFormsModule.withConfig({ warnOnNgModelWithFormControl: 'never' }),
    FormsModule,
    BrowserAnimationsModule,
    NgSelectModule,
    TreeModule,
    NgxDatatableModule,
    ToastrModule.forRoot(/* { preventDuplicates: true } */),
    ToastContainerModule,
    NgxNavbarModule,
    InfiniteScrollModule,
    CKEditorModule,
    Ng2TelInputModule,
    MatMomentDateModule,
    MomentDateModule,
    NgChartsModule,
    ScrollingModule,
    NgxMaterialTimepickerModule,
    FormsModule,
    NgxDaterangepickerMd.forRoot(),
    NgxMaskModule.forRoot(),
    ReactiveFormsModule,
    FlexLayoutModule,
    AngularSplitModule,
    OverlayModule,

    // DG Component Modules
    DgFileUploadLibModule,
    DgMenuLibModule,
    DgEmpSearchLibModule,
    DgTreeLibModule,
    DgListboxLibModule,
    DgOrgSearchLibModule,
    DgGridLibModule,
    DgCardGridLibModule,
    DgInputLibModule,
    DgDynamicGridLibModule,
    DgPostSearchLibModule,
    DgFabSpeedDialLibModule,
    DgNavBarLibModule,
    //application module
    AppRoutingModule,
    DgAddressLibModule,
    Bs4TooltipsModule,
    RecaptchaV3Module,
    DgNgCalendarLibModule,
    CommonPipeModule,
    DgSharedModule,

    //OIDC
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true,
      },
    }),

    //Footer
    footerModule,
  ],
  providers: [
    {
      provide: DG_ORG_INPUTS,
      useValue: { emptyCellText: '', showSearchIconInside: false },
    },
    DatePipe,
    TranslateService,
    RoleGuardService,
    AuthGuardService,
    AuthenticationService,
    CurrencyPipe,
    AppInitService,
    ComponentLoaderService,
    IntermediateSaveService,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (appInitService: AppInitService) =>
        appInitService?.appConfig?.captchaType === CaptchaType.RECAPTCHA_V3
          ? '6LdvCuEcAAAAAOxl35bnqCM5mO_HmICdfBbz3aHI'
          : null,
      deps: [AppInitService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [AppInitService],
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    HttpInterceptorProviders,
    ComponentConfig,
    StyleManagerService,
    InjectableRxStompConfig,
    RxStompService,
    StompWebsocketService,
    DSignStompWebsocketService,
    MainService,
    //OIDC
    {
      provide: OAuthStorage,
      useFactory: oauth2StorageFactory,
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useFactory: (appInitService: AppInitService) => {
        let locale;
        if (localStorage.getItem('appLocale')) {
          locale = JSON.parse(localStorage.getItem('appLocale'));
        }
        return locale.currency;
      },
      deps: [AppInitService],
    },
    {
      provide: LOCALE_ID,
      useFactory: (appInitService: AppInitService) => {
        let locale;
        if (localStorage.getItem('appLocale'))
          locale = JSON.parse(localStorage.getItem('appLocale'));
        return locale.locale;
      },
      deps: [AppInitService],
    },
    { provide: TitleStrategy, useClass: DgTitleStrategyService },

    {
      provide: 'ENC_SERVICE',
      useClass: EncryptionService,
    },
  ],
  exports: [CommonModule, TranslateModule],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {
  constructor(
    public appInitService: AppInitService,
    public translate: TranslateService
  ) {
    this.appInitService.languageSubject.subscribe((res) => {
      this.translate.setDefaultLang(res);
    });
  }
}
